import clsx from "clsx";
import { cva, VariantProps } from "cva";
import { ReactNode, useEffect, useId, useState } from "react";

import { Checkbox } from "../Checkbox";
import { Chip } from "../Chip";
import { CheckOvalIcon } from "../Icon";
import { Radio } from "../Radio";
import { Text, type Variant as TextVariant } from "../Text";
import styles from "./index.module.css";

export const variants = cva({
  variants: {
    variant: {
      distant: styles.distant,
    },
    completed: {
      true: styles.completed,
    },
    disabled: {
      true: styles.disabled,
    },
    error: {
      true: styles.error,
    },
  },
});

type InputOption = {
  label: ReactNode;
  value: string;
  disabled?: boolean;
};

export type InputOptionListProps = VariantProps<typeof variants> & {
  name: string;
  className?: string;
  required?: boolean;
  hideChip?: boolean;
  hideMessage?: boolean;
  disabled?: boolean;
  completed?: boolean;
  label?: ReactNode;
  info?: string;
  message?: string | ReadonlyArray<string>;
  options: InputOption[];
  type: "checkbox" | "radio";
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
};

export const InputOptionList = ({
  type,
  name,
  label,
  info,
  required,
  hideChip,
  hideMessage = false,
  disabled,
  completed,
  className,
  variant,
  message = "\u00A0",
  error,
  options,
  value,
  onChange,
}: Readonly<InputOptionListProps>) => {
  const [selectedValue, setSelectedValue] = useState(value);
  const controlId = useId();
  const messageId = `describe-${controlId}`;
  const messages = Array.isArray(message) ? message : [message];
  // 空白だけのメッセージを説明として紐付けると、VoiceOver が「スペース」と読み上げるため紐付けない
  const describedBy = !hideMessage && messages.some((text) => text.trim() !== "") ? messageId : undefined;
  const messageVariant: TextVariant = error ? "caution" : "weak";

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleCheckboxChange = (nextValue: string) => {
    const previous = Array.isArray(selectedValue) ? selectedValue : [];
    const newValue = previous.includes(nextValue)
      ? previous.filter((value) => value !== nextValue)
      : [...previous, nextValue];

    setSelectedValue(newValue);
    onChange?.(newValue);
  };

  const handleRadioChange = (nextValue: string) => {
    setSelectedValue(nextValue);
    onChange?.(nextValue);
  };

  return (
    <fieldset className={clsx(styles.container, variants({ variant }), className)} aria-describedby={describedBy}>
      <legend className={clsx(styles.legend, styles.label, variants({ variant, completed, disabled, error }))}>
        {label}
        {!(hideChip || completed || disabled) && (
          <Chip className={styles.labelChip} color={required ? "caution" : "weak"} text={required ? "必須" : "任意"} />
        )}
        {completed && (
          <span className={styles.completedIcon}>
            <CheckOvalIcon size="s" color="secondary" aria-hidden={true} />
          </span>
        )}
      </legend>
      {variant === "distant" && info && <p className={styles.info}>{info}</p>}
      <ul className={clsx(variants({ variant, completed, disabled, error }), styles.inputWrapper)}>
        {options.map((option) => (
          <li key={option.value}>
            {type === "checkbox" && (
              <Checkbox
                id={`${controlId}-${option.value}`}
                name={name}
                checked={Array.isArray(selectedValue) && selectedValue.includes(option.value)}
                disabled={completed || disabled || option.disabled}
                value={option.value}
                aria-describedby={describedBy}
                onChange={(event) => handleCheckboxChange(event.target.value)}
              >
                {option.label}
              </Checkbox>
            )}
            {type === "radio" && (
              <Radio
                id={`${controlId}-${option.value}`}
                name={name}
                checked={selectedValue === option.value}
                disabled={completed || disabled || option.disabled}
                value={option.value}
                aria-invalid={error || undefined}
                aria-describedby={describedBy}
                onChange={(event) => handleRadioChange(event.target.value)}
              >
                {option.label}
              </Radio>
            )}
          </li>
        ))}
      </ul>
      {!hideMessage && (
        <div id={messageId} aria-live="polite" className={styles.message}>
          {messages.map((messageText, index) => (
            <Text key={index} variant={messageVariant}>
              {messageText}
            </Text>
          ))}
        </div>
      )}
    </fieldset>
  );
};
